import React, { Fragment, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Form from './Form';
import PenForm from './PenForm';
import FrontCanvas from './FrontCanvas';
import './PromotionalEditor.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as selectors from './PromotionalEditor.selectors';
import * as actions from './PromotionalEditor.actions';
import { stringifyEqualityCheck } from './../../utils';
import * as loginSelectors from './../Login/Login.selectors';
import { Link } from 'react-router-dom';

const PromotionalEditor = (props) => {
    /* State to props */
    const templateCode = useSelector(selectors.getTemplateCode);
    const quantity = useSelector(selectors.getQuantity);
    const logo = useSelector(selectors.getLogo);
    const promotionalItem = useSelector(selectors.getPromotionalItem, stringifyEqualityCheck);
    const fetching = useSelector(selectors.getFetching);
    const addingToCart = useSelector(selectors.getAddingToCart);
    const department = useSelector(selectors.getDepartment, stringifyEqualityCheck);
    const fetchingDepartment = useSelector(selectors.getFetchingDepartment);
    const cxmlData = useSelector(loginSelectors.getCxmlData, stringifyEqualityCheck);
    const clinicName = useSelector(selectors.getClinicName);
    const phone = useSelector(selectors.getPhone);
    const ink = useSelector(selectors.getInk);
    const notes = useSelector(selectors.getNotes);
    const isFavorite = useSelector(selectors.getIsFavorite);
    const checkingFavorite = useSelector(selectors.getCheckingFavorite);
    const savingWork = useSelector(selectors.getSavingWork);
    const action = useSelector(selectors.getAction);
    const platformType = useSelector(loginSelectors.getPlatformType, stringifyEqualityCheck);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const fetchPromotionalItem = useCallback((id, departmentId) => dispatch(actions.fetchPromotionalItem(id, departmentId)), [dispatch]);
    const setTemplateCode = useCallback((value) => dispatch(actions.setTemplateCode(value)), [dispatch]);
    const setQuantity = useCallback((value) => dispatch(actions.setQuantity(value)), [dispatch]);
    const setLogo = useCallback((value) => dispatch(actions.setLogo(value)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const addToCart = useCallback((orderType, history, isMyuth, type) => dispatch(actions.addToCart(orderType, history, isMyuth, type)), [dispatch]);
    const fetchDepartment = useCallback((id) => dispatch(actions.fetchDepartment(id)), [dispatch]);
    const setClinicName = useCallback((value) => dispatch(actions.setClinicName(value)), [dispatch]);
    const setPhone = useCallback((value) => dispatch(actions.setPhone(value)), [dispatch]);
    const setInk = useCallback((value) => dispatch(actions.setInk(value)), [dispatch]);
    const setNotes = useCallback((value) => dispatch(actions.setNotes(value)), [dispatch]);
    const addFavorite = useCallback((history) => dispatch(actions.addFavorite(history)), [dispatch]);
    const checkFavorite = useCallback((templateCode) => dispatch(actions.checkFavorite(templateCode)), [dispatch]);
    const setAction = useCallback((value) => dispatch(actions.setAction(value)), [dispatch]);
    const loadVariables = useCallback(() => dispatch(actions.loadVariables()), [dispatch]);

    /* componentDidMount */
    useEffect(() => {
        resetState();
        setTemplateCode(props.match.params.id);
        checkFavorite(props.match.params.id);
        fetchPromotionalItem(props.match.params.id, props.match.params.departmentId);
        fetchDepartment(props.match.params.departmentId);

        if (props.match.params.action === 'edit') {
            loadVariables();
            setAction('edit');
        } else if (props.match.params.action === 'reorder') {
            loadVariables();
            setAction('reorder');
        }

        if (parseInt(props.match.params.id) === 16) {
            setLogo(1)
        }

        return () => {
            resetState();
        };
    }, [setTemplateCode, resetState, fetchPromotionalItem, fetchDepartment, checkFavorite, loadVariables, setAction, setLogo,
        props.match.params.id, props.match.params.departmentId, props.match.params.action]);

    let content = <div className="text-center"><i className="fas fa-circle-notch fa-spin" /> Loading data...</div>;
    if (!fetching) {
        if (parseInt(props.match.params.id) === 20) {
            content = (
                <div className="steps-content">
                    <PenForm
                        quantity={quantity}
                        logo={logo}
                        cxmlData={cxmlData}
                        templateCode={templateCode}
                        promotionalItem={promotionalItem}
                        fetching={fetching}
                        addingToCart={addingToCart}
                        department={department}
                        fetchingDepartment={fetchingDepartment}
                        clinicName={clinicName}
                        phone={phone}
                        ink={ink}
                        notes={notes}
                        isMyuth={props.match.params.isMyuth}
                        history={props.history}
                        productId={props.match.params.productId}
                        setQuantity={setQuantity}
                        setLogo={setLogo}
                        addToCart={addToCart}
                        setClinicName={setClinicName}
                        setPhone={setPhone}
                        setInk={setInk}
                        setNotes={setNotes}
                        isFavorite={isFavorite}
                        addFavorite={addFavorite}
                        checkingFavorite={checkingFavorite}
                        savingWork={savingWork}
                        action={action}
                    />
                </div>
            );
        } else {
            content = (
                <div className="steps-content">
                    <div className="left-side-container">
                        <Form
                            quantity={quantity}
                            logo={logo}
                            cxmlData={cxmlData}
                            templateCode={templateCode}
                            promotionalItem={promotionalItem}
                            fetching={fetching}
                            addingToCart={addingToCart}
                            department={department}
                            fetchingDepartment={fetchingDepartment}
                            isMyuth={props.match.params.isMyuth}
                            history={props.history}
                            productId={props.match.params.productId}
                            setQuantity={setQuantity}
                            setLogo={setLogo}
                            addToCart={addToCart}
                            isFavorite={isFavorite}
                            addFavorite={addFavorite}
                            checkingFavorite={checkingFavorite}
                            savingWork={savingWork}
                            action={action}
                            user={user}
                            templateId={props.match.params.id}
                        />
                    </div>
                    <div className="right-side-container">
                        <FrontCanvas
                            templateCode={templateCode}
                            logo={logo}
                            isMyuth={props.match.params.isMyuth}
                            templateId={props.match.params.id}
                            promotionalItem={promotionalItem}
                            fetching={fetching}
                            user={user}
                        />
                    </div>
                </div>
            );
        }
    }

    let breadcrumbsContainer = null;
    if (parseInt(props.match.params.productId) === 8 && promotionalItem != null) {
        if (props.match.params.isMyuth && parseInt(props.match.params.isMyuth) === 1) {
            const breadcrumbs = [<Link to={'/' + platformType}>Main Categories</Link>, ' / '];
            breadcrumbs.push(<Link to="/product/14">MyUTHealth</Link>);
            breadcrumbs.push(' / ');
            breadcrumbs.push(promotionalItem.name);
            breadcrumbsContainer = (
                <div className="breadcrumbs">/ {breadcrumbs}</div>
            );
        } else {
            const breadcrumbs = [<Link to="/utp">Main Categories</Link>, ' / '];
            breadcrumbs.push(<Link to="/department/8/377">Promotional</Link>);
            breadcrumbs.push(' / ');
            breadcrumbs.push(promotionalItem.name);
            breadcrumbsContainer = (
                <div className="breadcrumbs">/ {breadcrumbs}</div>
            );
        }
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-paint-brush"></i> Editor</Fragment>}
            step={3}
            history={props.history}
        >
            <Helmet><title>Editor - UT Physicians</title></Helmet>
            <div className="promotional-editor-container">
                {breadcrumbsContainer}
                {content}
            </div>
        </Layout>
    );
};

export default PromotionalEditor;
